"use client"

import { useTranslation } from "../../hooks/use-translation"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { useAppDispatch, useAppSelector } from "../../redux/hooks"
import {
  Bell,
  CreditCard,
  MessageSquare,
  Newspaper,
  Percent,
  Ticket,
  Users,
  FileText,
  ShieldAlert,
  Landmark,
  UserPlus,
} from "lucide-react"
import { useEffect } from "react"
import { fetchBuildings } from "../../redux/slices/buildingsSlice"
import { useAuth } from "../../contexts/auth-context"

interface SidebarProps {
  isMobile: boolean
  setMobileMenuOpen: (open: boolean) => void
}

export function Sidebar({ isMobile, setMobileMenuOpen }: SidebarProps) {
  const { buildings } = useAppSelector((state) => state.buildings)

  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const location = useLocation()
  const { user } = useAuth() // Use the auth context logout

  const isAdmin = user?.role === "admin"
  const managedTicketCategories = user?.managedTicketCategories
  const isCategoryOnlyAdmin = isAdmin && managedTicketCategories && managedTicketCategories.length > 0

  // Add this useEffect to fetch buildings data for all users
  useEffect(() => {
    dispatch(fetchBuildings(false)) // Only fetch active buildings
  }, [dispatch])

  // Improve the getBuildingName function to handle cases where buildings data isn't loaded yet
  const getBuildingName = (buildingId: string) => {
    if (!buildings || buildings.length === 0) {
      return buildingId // Return the ID if buildings data isn't available
    }
    const building = buildings.find((b) => b.id === buildingId)
    return building ? building.name : buildingId
  }

  const links = [
    {
      href: "/dashboard",
      label: t("announcements"),
      icon: <Bell className="size-5" />,
    },
    {
      href: "/dashboard/tickets",
      label: t("tickets"),
      icon: <Ticket className="size-5" />,
    },
    {
      href: "/dashboard/messages",
      label: t("messages"),
      icon: <MessageSquare className="size-5" />,
      badge: undefined,
    },
    {
      href: "/dashboard/blog",
      label: t("blog"),
      icon: <Newspaper className="size-5" />,
    },
    {
      href: "/dashboard/membership-card",
      label: t("membershipCard"),
      icon: <CreditCard className="size-5" />,
    },
    {
      href: "/dashboard/discounts",
      label: t("discounts"),
      icon: <Percent className="size-5" />,
    },
    {
      href: "/dashboard/guests",
      label: t("guestRegistration"),
      icon: <UserPlus className="size-5" />,
    },
  ]

  // Admin-only links
  const adminLinks = [
    {
      href: "/dashboard/admin/guests",
      label: t("guestManagement"),
      icon: <UserPlus className="size-5" />,
      showForCategoryAdmin: false,
    },
    {
      href: "/dashboard/admin/users",
      label: t("userManagement"),
      icon: <Users className="size-5" />,
      showForCategoryAdmin: false,
    },
    {
      href: "/dashboard/admin/tickets",
      label: t("ticketManagement"),
      icon: <Ticket className="size-5" />,
      showForCategoryAdmin: true,
    },
    {
      href: "/dashboard/admin/messages",
      label: t("messageManagement"),
      icon: <MessageSquare className="size-5" />,
      showForCategoryAdmin: false,
    },
    {
      href: "/dashboard/admin/announcements",
      label: t("announcementManagement"),
      icon: <Bell className="size-5" />,
      showForCategoryAdmin: false,
    },
    {
      href: "/dashboard/admin/blog",
      label: t("blogManagement"),
      icon: <FileText className="size-5" />,
      showForCategoryAdmin: false,
    },
    {
      href: "/dashboard/admin/buildings",
      label: t("buildingManagement"),
      icon: <Landmark className="size-5" />,
      showForCategoryAdmin: false,
    },
    {
      href: "/dashboard/admin/discounts",
      label: t("discountManagement"),
      icon: <Percent className="size-5" />,
      showForCategoryAdmin: false,
    },
  ]

  const handleLinkClick = () => {
    if (isMobile) {
      setMobileMenuOpen(false)
    }
  }

  return (
    <div className="sidebar d-flex flex-column h-100" style={{ paddingTop: "60px" }}>
      <div className="flex-grow-1 overflow-auto py-2">
        <nav className="d-grid gap-1 px-2">
          {links.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              onClick={handleLinkClick}
              className={`sidebar-link ${location.pathname === link.href ? "active" : ""}`}
            >
              {link.icon}
              <span>{link.label}</span>
              {link.badge && <span className="sidebar-badge">{link.badge}</span>}
            </Link>
          ))}

          {isAdmin && (
            <>
              <div className="sidebar-section mt-4 mb-3">
                <div className="d-flex align-items-center gap-2 px-3 py-2">
                  <ShieldAlert size={16} className="text-secondary" />
                  <span className="text-secondary fw-medium">{t("adminSection")}</span>
                </div>
              </div>
              {adminLinks
                .filter((link) => !isCategoryOnlyAdmin || link.showForCategoryAdmin)
                .map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={handleLinkClick}
                    className={`sidebar-link ${location.pathname === link.href ? "active" : ""}`}
                  >
                    {link.icon}
                    <span>{link.label}</span>
                  </Link>
                ))}
            </>
          )}
        </nav>
      </div>
    </div>
  )
}
